import React, { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { ConditionalChildren, ContainerLayout } from 'layouts';
import { TranslationContext } from '@ds/react-utils';
import { apiService, configuration } from 'services';
import { ContinueModel } from '../../../types/ContinueModel';
import { useButtonsLoading } from '../../../hooks';
import { WarningIcon } from 'components/shared';

export type SupportedErrorType = 'AttemptsExceeded' | 'RejectedInput' | 'Unspecified';

const DocumentLockedPage = (): ReactElement => {
    const translate = React.useContext(TranslationContext);

    const { isPrimaryButtonLoading, setIsPrimaryButtonLoading } = useButtonsLoading();

    const enableErrorCallback = configuration.enableErrorCallback();
    const isOrchestration = configuration.isOrchestration ? configuration.isOrchestration() : false;
    const [errorContinueModel, setErrorContinueModel] = useState<ContinueModel | undefined>(undefined);
    const formRef = useRef<HTMLFormElement>(null);
    const orchestrationRedirectInvokedRef = useRef<boolean>(false);

    const errorType: SupportedErrorType = 'AttemptsExceeded';

    const onCallbackClick = useCallback(() => {
        setIsPrimaryButtonLoading(true);

        apiService.Get<ContinueModel>('App/continue/error')
            .then(response => {
                setErrorContinueModel(response);
            });
    }, [setIsPrimaryButtonLoading]);

    useEffect(() => {
        if (formRef.current && errorContinueModel) {
            formRef.current.submit();
        }
    }, [formRef, errorContinueModel]);

    // If orchestration flow, immediately invoke the error callback (guarded to run only once)
    useEffect(() => {
        if (isOrchestration && enableErrorCallback && !orchestrationRedirectInvokedRef.current) {
            orchestrationRedirectInvokedRef.current = true;
            onCallbackClick();
        }
    }, [isOrchestration, enableErrorCallback, onCallbackClick]);

    const HiddenErrorForm = errorContinueModel ? (
        <form method="POST" action={errorContinueModel.callbackUri} ref={formRef} data-qa="verify-document-locked-form">
            <input type="hidden" name="session_id" value={errorContinueModel.sessionId} data-qa="verify-document-locked-form-sessionid" />
            <input type="hidden" name="error" value={errorType} data-qa="verify-document-locked-form-error" />
        </form>
    ) : null;

    // If orchestration flow, don't show the locked page content since we're immediately redirecting
    // Only render the hidden form so the POST can occur
    if (isOrchestration && enableErrorCallback) {
        return (
            <div>
                {HiddenErrorForm}
            </div>
        );
    }

    return (
        <ContainerLayout>
            <ContainerLayout.Header>
                <WarningIcon dataQa='verify-document-locked-warning-icon' />
                <ContainerLayout.Header.Title
                    titleDataQa="verify-document-locked-title"
                    title={translate("You've reached the authentication limit")}
                />
            </ContainerLayout.Header>

            <ContainerLayout.InnerContent dataQa="verify-document-locked-content">
                <p>{translate('You have made the maximum number of attempts allowed.')}</p>
                <p>{translate('We have notified the person or organization requesting this identity verification.')}</p>
            </ContainerLayout.InnerContent>

            <ConditionalChildren condition={enableErrorCallback}>
                {HiddenErrorForm}
                <ContainerLayout.Footer>
                    <ContainerLayout.Footer.Buttons>
                        <ContainerLayout.Footer.Buttons.Primary
                            data-qa="verify-document-locked-primary-button"
                            text={translate('Next')}
                            onClick={onCallbackClick}
                            loading={isPrimaryButtonLoading}
                        />
                    </ContainerLayout.Footer.Buttons>
                </ContainerLayout.Footer>
            </ConditionalChildren>
        </ContainerLayout>
    );
};

export default DocumentLockedPage;
